// src/utils/tabFilters.ts
//
// Which dimension filters reach which tile on the ACTIVE tab. Pure, so the
// routing rules are testable without mounting Insights (plan 2026-09-21
// control block, S8). Insights calls it through a thin wrapper that binds
// the page's state.
//
// Two writers set tab filters today (DashboardTabFilterContext): the funnel
// tile's channel click, and a control block (the attribution-model dropdown).
// They are routed by different rules:
//
// - An ordinary tab filter on dimension D applies to every tile EXCEPT the
//   one it came from (its `source`, plan 2026-10-05 dashboard interactivity
//   B3): the clicked tile keeps showing every value and highlights the
//   chosen one (the funnel's channel table keeps every channel while the
//   rest of the tab filters on the clicked one; Arjen, 28-08). Another tile
//   that also breaks down by D IS filtered. A filter without a source
//   exempts no tile. (Until 2026-10-05 the exemption was "every tile with D
//   in `chart_spec_json.dimension_keys`", which also exempted a pivot with D
//   as a row from the bar's and every other tile's filter on D.)
// - A tab filter on a CONTROL dimension (one a control tile on this tab
//   steers) reaches ONLY the tiles whose `control_dimensions` names it. The
//   backend decides that list per tile (its dataset carries the dimension and
//   its query does not pin it itself), so a tile without it never gets the
//   value, never turns transient and keeps serving from the cache.
//
// Dashboard-wide filters (the connector picker and the filter bar) apply to
// every tile that can take them.
//
// Reach (plan 2026-10-05 dashboard interactivity, A2/A4): a tile lists the
// viewer filters that reach it in `filterable_dimensions`. A dashboard or
// ordinary tab filter on a key that list lacks is dropped for that tile, so
// a tile no active filter reaches carries the unfiltered set and keeps
// serving from its cache. A payload without the field keeps the old rule
// (every dashboard filter, every tab filter). Control dimensions keep their
// own rule above and are never dropped for reach.
//
// A bar filter and a tab (click) filter on the same key INTERSECT: a click
// narrows the bar's selection, it never escapes it.
//
// Author-scoped interactions (B5): a source tile may name the tiles its
// clicks filter (`chart_spec_json.interactions.targets`, block keys). A click
// filter from such a tile then reaches only the tiles on that list, still
// subject to reach above. Absent or "auto" keeps the default.
//
// Open controls (ADR-0350 decision 3) are a layer of their own, never a tab
// filter: one control choice per control, ANDed with the bar, the clicks and
// every other control on the same field. A control is no data tile, so it
// exempts no tile (the source rule above is for clicks only); it reaches the
// tiles of its own stored `interactions.targets` (absent = the whole page),
// still subject to reach. An EMPTY intersection means the tile shows nothing
// (`tileFilters(...).emptiedBy`), never the wider set: the bar/click rule's
// fallback in `intersect` does not apply to a control.
import type { DashboardItemDTO, DimensionFilters } from "@/api/dashboards";
import { controlDimensionOfSpec, openFieldControlOfSpec } from "@/components/dashboards/composition/blocks";

export type TileLike = Pick<DashboardItemDTO, "control_dimensions" | "filterable_dimensions"> & {
  /** The tile's id; a tab filter whose source it is skips it. */
  id?: string;
  /** The tile's block key; an author's interaction targets name it. */
  block_key?: string;
  chart_spec_json?: Record<string, unknown> | null;
};

/** The keys a tile says viewer filters reach, or null when the payload
 *  predates the field (then nothing is dropped for reach). */
function reachOf(item: Pick<DashboardItemDTO, "filterable_dimensions">): Set<string> | null {
  return item.filterable_dimensions ? new Set(item.filterable_dimensions.map((d) => d.key)) : null;
}

/** The values of a bar filter narrowed by a tab filter on the same key. An
 *  empty intersection cannot come from a click (every value a reached tile
 *  shows is inside the bar's selection), only from a bar change after the
 *  click; the bar wins then, because an empty list would read as "no filter"
 *  everywhere downstream. */
function intersect(bar: string[], tab: string[]): string[] {
  const allowed = new Set(bar);
  const both = tab.filter((v) => allowed.has(v));
  return both.length ? both : bar;
}

/** The dimensions the control tiles among `items` steer. Read off each
 *  control's stored spec (see `controlDimensionOfSpec`). */
export function controlDimensionsOf(
  items: ReadonlyArray<{ chart_spec_json?: Record<string, unknown> | null }>,
): Set<string> {
  const dims = new Set<string>();
  for (const item of items) {
    const dim = controlDimensionOfSpec(item.chart_spec_json ?? null);
    if (dim) dims.add(dim);
  }
  return dims;
}

/** The tiles a click on a tile with this spec filters: "auto" (the
 *  default, also for anything malformed) or the block keys the author chose. */
export function interactionTargetsOf(spec: Record<string, unknown> | null | undefined): "auto" | string[] {
  const value = spec?.interactions as { targets?: unknown } | undefined;
  const targets = value && typeof value === "object" ? value.targets : undefined;
  return Array.isArray(targets) ? targets.filter((t): t is string => typeof t === "string") : "auto";
}

/** Source tile id -> the block keys its click filters may reach, for the
 *  tiles among `items` whose author narrowed them. */
export function interactionScopesOf(
  items: ReadonlyArray<{ id: string; chart_spec_json?: Record<string, unknown> | null }>,
): Record<string, ReadonlySet<string>> {
  const scopes: Record<string, ReadonlySet<string>> = {};
  for (const item of items) {
    const targets = interactionTargetsOf(item.chart_spec_json);
    if (targets !== "auto") scopes[item.id] = new Set(targets);
  }
  return scopes;
}

/** One open control's current choice (ADR-0350). */
export type ControlChoice = {
  /** The control item's id. */
  id: string;
  dimension: string;
  /** Empty = the control filters nothing right now. */
  values: readonly string[];
  /** "auto" (every tile on the page) or the block keys its author chose. */
  scope: "auto" | ReadonlySet<string>;
};

/** The choices of the open field controls among `items` (one tab's), each
 *  from `choices` (block key -> values, `resolveControlChoiceParams`) or,
 *  when absent, the control's default. A control without a field, or with
 *  an empty choice, is left out: it filters nothing. */
export function controlChoicesOf(
  items: ReadonlyArray<{ id: string; block_key?: string; chart_spec_json?: Record<string, unknown> | null }>,
  choices: Readonly<Record<string, readonly string[]>>,
): ControlChoice[] {
  const out: ControlChoice[] = [];
  for (const item of items) {
    const control = openFieldControlOfSpec(item.chart_spec_json ?? null);
    if (!control?.dimension) continue;
    const values = (item.block_key !== undefined ? choices[item.block_key] : undefined) ?? control.default;
    if (!values.length) continue;
    const targets = interactionTargetsOf(item.chart_spec_json);
    out.push({
      id: item.id,
      dimension: control.dimension,
      values,
      scope: targets === "auto" ? "auto" : new Set(targets),
    });
  }
  return out;
}

export type TileFilters = {
  /** What the tile's refresh carries. Never holds an empty list. */
  filters: DimensionFilters;
  /** Fields on which the open controls reaching this tile leave no value
   *  (two controls, or a control and the bar, that do not overlap). Non-empty
   *  = the tile shows "no data for this selection" and must not query. */
  emptiedBy: string[];
};

/** The filter set one tile's refresh carries. */
export function filtersForTile(
  item: TileLike,
  dashboardFilters: DimensionFilters,
  tabFilters: DimensionFilters,
  controlDimensions: ReadonlySet<string>,
  /** Dimension -> the tile a tab filter came from (click filters only). */
  tabSources: Readonly<Record<string, string>> = {},
  /** Source tile id -> the block keys it may reach (`interactionScopesOf`). */
  sourceScopes: Readonly<Record<string, ReadonlySet<string>>> = {},
): DimensionFilters {
  return barAndTabFilters(item, dashboardFilters, tabFilters, controlDimensions, tabSources, sourceScopes);
}

/** `filtersForTile` with the open controls' layer on top, and whether they
 *  empty the tile. The ONLY entry that takes control choices: a caller with
 *  controls must handle `emptiedBy`, so no wrapper may drop it (82's review,
 *  2026-10-06). */
export function tileFilters(
  item: TileLike,
  dashboardFilters: DimensionFilters,
  tabFilters: DimensionFilters,
  controlDimensions: ReadonlySet<string>,
  tabSources: Readonly<Record<string, string>> = {},
  sourceScopes: Readonly<Record<string, ReadonlySet<string>>> = {},
  controlChoices: ReadonlyArray<ControlChoice> = [],
): TileFilters {
  const merged = barAndTabFilters(item, dashboardFilters, tabFilters, controlDimensions, tabSources, sourceScopes);
  const reach = reachOf(item);
  const emptiedBy: string[] = [];
  let filters = merged;
  for (const choice of controlChoices) {
    if (!choice.values.length || choice.id === item.id) continue;
    if (choice.scope !== "auto" && !inScope(choice.scope, item.block_key)) continue;
    // A payload without reach keeps the old rule for clicks and the bar;
    // a control, new with reach, never steers a tile that does not list it.
    if (!reach || !reach.has(choice.dimension)) continue;
    if (filters === merged) filters = { ...merged };
    const current = filters[choice.dimension];
    if (!current) {
      filters[choice.dimension] = [...choice.values];
      continue;
    }
    const allowed = new Set(choice.values);
    const both = current.filter((v) => allowed.has(v));
    if (both.length) {
      filters[choice.dimension] = both;
    } else if (!emptiedBy.includes(choice.dimension)) {
      // Never an empty list on the wire (the backend reads it as "no
      // filter"); the caller shows the empty state instead of querying.
      emptiedBy.push(choice.dimension);
    }
  }
  return { filters, emptiedBy };
}

function barAndTabFilters(
  item: TileLike,
  dashboardFilters: DimensionFilters,
  tabFilters: DimensionFilters,
  controlDimensions: ReadonlySet<string>,
  tabSources: Readonly<Record<string, string>>,
  sourceScopes: Readonly<Record<string, ReadonlySet<string>>>,
): DimensionFilters {
  const reach = reachOf(item);
  const tabKeys = Object.keys(tabFilters);
  if (!tabKeys.length && !reach) return dashboardFilters;
  const merged: DimensionFilters = {};
  for (const [dim, values] of Object.entries(dashboardFilters)) {
    if (reach && !reach.has(dim)) continue;
    merged[dim] = values;
  }
  const steeredBy = item.control_dimensions ?? [];
  for (const dim of tabKeys) {
    if (controlDimensions.has(dim)) {
      if (!steeredBy.includes(dim)) continue;
    } else if ((item.id !== undefined && tabSources[dim] === item.id) || (reach && !reach.has(dim))) {
      continue;
    } else if (tabSources[dim] !== undefined && !inScope(sourceScopes[tabSources[dim]], item.block_key)) {
      continue;
    }
    merged[dim] = merged[dim] ? intersect(merged[dim], tabFilters[dim]) : tabFilters[dim];
  }
  return merged;
}

/** The filters every one of `sets` carries: the same key with the same
 *  values (in any order) on each, values sorted. Empty for no sets. What a
 *  control with Only relevant values narrows its options by (plan
 *  2026-10-09 button slicer B2): a filter that reaches only some of its
 *  charts would hide values the other charts still show. */
export function commonFilters(sets: ReadonlyArray<DimensionFilters>): DimensionFilters {
  if (!sets.length) return {};
  const [first, ...rest] = sets;
  const out: DimensionFilters = {};
  for (const [key, values] of Object.entries(first)) {
    const sorted = [...new Set(values)].sort();
    const same = rest.every((set) => {
      const other = set[key];
      if (!other) return false;
      const theirs = [...new Set(other)].sort();
      return theirs.length === sorted.length && theirs.every((v, i) => v === sorted[i]);
    });
    if (same) out[key] = sorted;
  }
  return out;
}

/** Does a source with this scope reach a tile with this block key? No
 *  scope = auto. A tile without a block key (a payload that predates it) is
 *  outside every explicit list. */
function inScope(scope: ReadonlySet<string> | undefined, blockKey: string | undefined): boolean {
  return scope === undefined || (blockKey !== undefined && scope.has(blockKey));
}

/** The active dashboard-wide filter keys that do NOT reach `item`, for its
 *  "Not filtered by X" note. Empty when the payload predates
 *  `filterable_dimensions` (nothing is known to be skipped). */
export function filterKeysNotReaching(
  item: Pick<DashboardItemDTO, "filterable_dimensions">,
  activeKeys: Iterable<string>,
): string[] {
  const reach = reachOf(item);
  if (!reach) return [];
  return [...activeKeys].filter((key) => !reach.has(key));
}

/** The tab's CLICK filters (`tabSources`: dimension -> source tile id) that
 *  do NOT reach `item`, for the same note: the tile lacks the dimension, or
 *  the source's author scoped it out (B5). Never on the source tile itself,
 *  which keeps every value on purpose (B3). Empty when the payload predates
 *  `filterable_dimensions`. */
export function clickFilterKeysNotReaching(
  item: Pick<DashboardItemDTO, "filterable_dimensions"> & { id: string; block_key?: string },
  tabSources: Readonly<Record<string, string>>,
  sourceScopes: Readonly<Record<string, ReadonlySet<string>>>,
): string[] {
  const reach = reachOf(item);
  if (!reach) return [];
  return Object.entries(tabSources)
    .filter(([dim, source]) => source !== item.id && (!reach.has(dim) || !inScope(sourceScopes[source], item.block_key)))
    .map(([dim]) => dim);
}

export type AddFilterOption = {
  key: string;
  label: string;
  /** Tiles this dimension reaches, out of `total`. */
  count: number;
  total: number;
};

/** The "Add filter" list: every dimension any of `items` (the dashboard's
 *  data tiles) can be filtered on, with how many tiles it reaches, most
 *  covering first, then by label. `exclude` holds what may not be added here:
 *  control dimensions, keys the connector picker owns, keys already active. */
export function addFilterOptions(
  items: ReadonlyArray<Pick<DashboardItemDTO, "filterable_dimensions">>,
  exclude: ReadonlySet<string>,
): AddFilterOption[] {
  const byKey = new Map<string, { label: string; count: number }>();
  for (const item of items) {
    const seen = new Set<string>();
    for (const { key, label } of item.filterable_dimensions ?? []) {
      if (exclude.has(key) || seen.has(key)) continue;
      seen.add(key);
      const entry = byKey.get(key);
      if (entry) entry.count += 1;
      else byKey.set(key, { label, count: 1 });
    }
  }
  return [...byKey.entries()]
    .map(([key, { label, count }]) => ({ key, label, count, total: items.length }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label) || (a.key < b.key ? -1 : 1));
}

/**
 * Which of `items` may be served from the unfiltered tab snapshot and which
 * must go to the filtered refresh (plan 2026-10-05, D, symptom 1). Decided per
 * tile on the set its own refresh would carry: a tile no active filter
 * reaches is the default view, whatever else on the tab is filtered.
 */
export function partitionBySnapshotEligibility<T>(
  items: ReadonlyArray<T>,
  filtersOf: (item: T) => DimensionFilters,
): { unfiltered: T[]; filtered: T[] } {
  const unfiltered: T[] = [];
  const filtered: T[] = [];
  for (const item of items) {
    (Object.keys(filtersOf(item)).length ? filtered : unfiltered).push(item);
  }
  return { unfiltered, filtered };
}

/** The tab filters the chip bar shows: every one except a control
 *  dimension's. The control IS the display of its value, and a chip's clear
 *  button would mean "no model at all". Empty = no chip bar. */
export function chipFilterEntries(
  tabFilters: DimensionFilters,
  controlDimensions: ReadonlySet<string>,
): Array<[string, string[]]> {
  return Object.entries(tabFilters).filter(([dim, values]) => !controlDimensions.has(dim) && values.length > 0);
}
